import { useParams, Link } from 'react-router-dom';
import { useGeneralExams, useExamStats } from '../lib/api';
import type { ExamSectionStat } from '../lib/api';
import { motion, PageTransition, Stagger, CountUp } from '../lib/motion';

// Rotating accent gradients so the topic list feels alive, assigned by
// position. Purely decorative.
const BAR_ACCENTS = [
  'from-teal-400 to-emerald-400',
  'from-indigo-400 to-purple-400',
  'from-amber-400 to-orange-400',
  'from-sky-400 to-blue-400',
  'from-rose-400 to-pink-400',
] as const;

export function ExamTopicsPage() {
  const { examId } = useParams();
  const { data: examsData } = useGeneralExams();
  const { data: statsData } = useExamStats(examId ?? null);

  const exam = examsData?.exams?.find(e => e.id === examId);
  const stats = statsData?.stats;

  return (
    <PageTransition className="max-w-lg mx-auto px-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-1 pt-1 mb-6">
        <Link
          to={`/exam/${examId}`}
          aria-label="Back to exam dashboard"
          className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full text-squishy-primary hover:bg-teal-50 transition-colors shrink-0"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
        </Link>
        <div>
          <h1 className="font-display text-xl font-semibold text-squishy-dark tracking-tight leading-tight">All Topics</h1>
          {exam && <p className="text-[11px] text-slate-500">{exam.code} · your coverage by topic</p>}
        </div>
      </div>

      {stats && stats.sections.length > 0 ? (
        <Stagger className="space-y-2.5">
          {stats.sections.map((s, i) => (
            <Stagger.Item key={s.sectionId}>
              <TopicRow stat={s} accent={BAR_ACCENTS[i % BAR_ACCENTS.length]!} />
            </Stagger.Item>
          ))}
        </Stagger>
      ) : (
        <div className="text-center py-16">
          <div className="mx-auto w-14 h-14 rounded-3xl bg-teal-50 flex items-center justify-center mb-4" aria-hidden>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor" className="w-6 h-6 text-squishy-primary">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-slate-500">No topics available.</p>
          <p className="text-xs text-slate-400 mt-1">Topics appear here once the exam has content.</p>
        </div>
      )}
    </PageTransition>
  );
}

function TopicRow({ stat, accent }: { stat: ExamSectionStat; accent: string }) {
  const progress = stat.totalQuestions > 0 ? Math.round((stat.attempted / stat.totalQuestions) * 100) : 0;
  const complete = progress >= 100;

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-card">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-sm text-squishy-dark truncate">{stat.sectionName}</span>
        <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide shrink-0">{stat.sectionCode}</span>
        {complete && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden>
              <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
            </svg>
            Complete
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 mt-2.5">
        <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <motion.div
            className={`h-full rounded-full bg-gradient-to-r ${accent}`}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <span className="text-xs font-bold text-squishy-dark tabular-nums shrink-0 w-10 text-right">
          <CountUp value={progress} />%
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-400">
        <span className="tabular-nums">{stat.attempted}/{stat.totalQuestions} answered</span>
        <span aria-hidden>{'·'}</span>
        <span>
          <span className={`font-semibold ${stat.accuracy >= 70 ? 'text-emerald-600' : stat.accuracy >= 50 ? 'text-amber-600' : 'text-slate-500'}`}>{stat.accuracy}%</span> accuracy
        </span>
      </div>
    </div>
  );
}
